<template>
  <div>
    <ToastPopup
      v-if="showCopyToast"
      status="info"
      text="copied to clipboard..."
      class="w-1/4"
      @close="showCopyToast = false"
    />

    <div
      v-if="showUpdateWidgetModal"
      class="fixed top-0 left-0 flex justify-center items-center w-full h-full bg-modal z-50"
    >
      <UpdateWidgetModal
        @discard-changes="discardChanges"
        @return-to-editing="returnToEditing"
      />
    </div>

    <UpdateWidgetPopup
      v-show="isUpdated"
      :widget-id="widget.id"
      @copied="copyToClipboard"
      @firstUpdateWidget="firstUpdatedWidget"
    />

    <UpdateWidget
      v-if="isOldWidget"
      @updateInfo="updateMyWidget"
    />

    <div id="editWidgetFlow" v-else>
      <div class="flex justify-between">
        <h1 class="mt-8 mb-6 text-lg">
          <span class="my-widgets-link" @click="$router.push('/')">
            {{ changeCase('my widgets', ElementE.TITLE) }}
          </span> / {{ widget.name }}
        </h1>

        <ToastPopup
          v-if="showErrorToast"
          status="error"
          :text="errorMessage"
          @close="showErrorToast = false"
        />

        <PropertyWarningModal
          v-if="isPropertyWarning"
          @cancel="isPropertyWarning = false"
          @confirm="saveWidget"
        />

        <SaveWidgetBtn @click.prevent="saveWidget" :visible="saveBtnVisibility" />
      </div>

      <div v-if="!isError">
        <TabMenuNew :active-index="activeIndex" @sendTabId="loadTab" />
        <GeneralTab
          v-show="activeTab === TabE.GENERAL"
          :is-properties-updated="isPropertiesUpdated"
          :widgets="widget"
          :paymentProp="paymentMethods"
          @updateWidget="updateWidget"
        />
        <PropertiesTab
          v-show="activeTab === TabE.PROPERTIES"
          :widget_info="widgetInfo"
          :widgets="widget"
          @updateWidget="updateWidget"
        />
        <DisplayTab
          v-show="activeTab === TabE.DISPLAY"
          :widgets="widget"
          @updateWidget="updateWidget"
        />
        <StyleTab
          v-show="activeTab === TabE.STYLE"
          :widgets="widget"
          @updateWidget="updateWidget"
        />
        <!-- We use v-if here cause we dont need to upload widget config
         before we go to preview tab, this will reduce the load on the system -->
        <PreviewTab
          v-if="activeTab === TabE.PREVIEW"
          :widgets="widget"
          @updateWidget="updateWidget"
          @copied="copyToClipboard"
        />
        <div class="flex justify-end gap-4 w-full">
          <PrevBtn v-show="activeTab !== TabE.GENERAL" @sendTabId="loadPrevTab" />
          <NextBtn v-show="activeTab !== TabE.PREVIEW" @sendTabId="loadNextTab" />
        </div>
      </div>
    </div>
  </div>

  <OverlaySpinner v-if="isLoading" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import axiosClient from "../axiosClient";
import store from '@/store';

import { WidgetI, PaymentMethodsI } from "@/interfaces/WidgetI";
import {
  WIDGET_FIELD_MAX_LENGTH as WIDGET_NAME_MAX_LENGTH,
  WIDGET_DESCRIPTION_MAX_LENGTH,
} from '../constants';
import { changeCase, validatePaymentConfig } from "../helpers";
import { ElementE, TabE, PaymentGatewayE } from '../enums';

import TabMenuNew from "../components/menus/TabMenuNew.vue";
import UpdateWidgetPopup from "@/components/notifications/UpdateWidgetPopup.vue";
import GeneralTab from "../components/edittabs/GeneralTab.vue";
import PropertiesTab from "../components/edittabs/PropertiesTab.vue";
import DisplayTab from "../components/edittabs/DisplayTab.vue";
import StyleTab from "../components/edittabs/StyleTab.vue";
import PreviewTab from "../components/edittabs/PreviewTab.vue";
import NextBtn from "../components/buttons/NextBtn.vue";
import PrevBtn from "../components/buttons/PrevBtn.vue";
import SaveWidgetBtn from "../components/buttons/SaveWidgetBtn.vue";
import UpdateWidget from "@/components/notifications/UpdateWidget.vue";
import ToastPopup from "../components/notifications/ToastPopup.vue";
import UpdateWidgetModal from "../components/modals/UpdateWidgetModal.vue";
import PropertyWarningModal from "../components/modals/PropertyWarningModal.vue";
import OverlaySpinner from '@/components/loader/OverlaySpinner.vue';

export default defineComponent({
  name: "EditWidget",
  components: {
    UpdateWidget,
    PrevBtn,
    TabMenuNew,
    GeneralTab,
    PropertiesTab,
    DisplayTab,
    StyleTab,
    PreviewTab,
    NextBtn,
    SaveWidgetBtn,
    UpdateWidgetPopup,
    ToastPopup,
    UpdateWidgetModal,
    PropertyWarningModal,
    OverlaySpinner,
  },

  beforeRouteLeave(to, from, next) {
    this.toPage = to.fullPath;

    if (this.saveBtnVisibility) {
      this.showUpdateWidgetModal = true;

      if (this.redirect) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },

  methods: {
    discardChanges() {
      this.showUpdateWidgetModal = false;
      this.redirect = true;
      this.$router.push(this.toPage);
    },

    returnToEditing() {
      this.showUpdateWidgetModal = false;
      this.redirect = false;
    },

    copyToClipboard() {
      this.showCopyToast = true;


      setTimeout(() => {
        this.showCopyToast = false;
      }, 3000);
    },

    firstUpdatedWidget(){
      this.widget.updated = 0;
      this.isUpdated = false;
      this.saveWidget();
    },
    loadTab(info: {tabName: TabE, index: number}) {
      if(this.isError) {
        return;
      }

      this.$nextTick(() => {
        this.activeIndex = info.index;
        this.activeTab = info.tabName;
      });
    },

    loadNextTab() {
      this.activeIndex += 1;
      this.$nextTick(() => {
        this.activeTab = Object.values(TabE)[this.activeIndex];
      });
    },

    loadPrevTab(){
      this.activeIndex -= 1;
      this.$nextTick(() => {
        this.activeTab = Object.values(TabE)[this.activeIndex];
      });
    },

    async loadWidget() {
      if(this.isError) {
        return;
      }

      await axiosClient.get("v4/widget/" + this.$route.params.wid).then(({ data }) => {
        const { payment, widget } = data;

        this.widget = widget;
        // Comment out CRDB payment gateway for now
        const filteredPayment = payment.filter(({ name }: { name: string }) => name !== PaymentGatewayE.CRDB);
        this.paymentMethods = filteredPayment;
        this.widget.edited = false;
        this.widget.config = JSON.parse(widget.config);

        if( parseInt(widget.version) == 3 ){
          this.isOldWidget = true;
        }

        if( parseInt(widget.updated) === 1 ){
          this.isUpdated = true;
        }

        sessionStorage.setItem(
          "payment_methods",
          JSON.stringify(payment)
        );
        sessionStorage.setItem(this.widget.id, JSON.stringify(this.widget));
      }).catch((error) => {
        if(error.response == undefined){
          this.errorMessage = 'Internal Server Error'
          return;
        }
        if(error.response.status === 500) {
          this.errorMessage = 'Internal Server Error'
        } else {
          this.errorMessage = 'Widget is not found'
        }
        this.isError = true;
      });
    },
    async loadWidgetInfoByApi() {
      await axiosClient.get("v3/dashboard").then((res) => {
        this.widgetInfo = res.data;
      }).catch((error) => {
        if(error.response == undefined){
          this.errorMessage = 'Internal Server Error'
          return;
        }
        if(error.response.status === 500) {
          this.errorMessage = 'Internal Server Error'
        } else {
          this.errorMessage = 'Widget is not found'
        }
        this.isError = true;
      });
    },

    updateWidget: function (widget: WidgetI) {
      if (widget.config.enable_card_payments) {
        this.isSaveWidget = false;


        if (widget.config.payment_gateway_api && widget.config.payment_gateway_config) {
          this.isSaveWidget = validatePaymentConfig(widget.config.payment_gateway_api, widget.config.payment_gateway_config);
        }
      } else {
        this.isSaveWidget = true;
      }

      // if we disable booking, but leave some data in payemnt config, still enable saving widget
      if (!widget.config.enable_bookings && widget.config.enable_card_payments) {
        this.isSaveWidget = true;
      }

      if (
        !widget.name
        || widget.name.length > WIDGET_NAME_MAX_LENGTH
        || (widget.description && widget.description.length > WIDGET_DESCRIPTION_MAX_LENGTH)
        || !this.isSaveWidget
      ) {
        this.widget.edited = false;
        this.isSaveLabel = false;
        this.saveBtnVisibility = false;

        return;
      }


      this.widget = widget;
      this.widget.edited = true;
      this.isSaveLabel = true;
      this.saveBtnVisibility = true;
    },

    updateMyWidget(){
      this.widget.version = '4.0';
      this.widget.updated = '1';
      this.saveWidget();
    },
    async saveWidget() {
      const isPropertyTab = this.activeTab === TabE.PROPERTIES;
      this.isPropertiesUpdated = false;

      if (isPropertyTab && !this.widget.config.principals.length && !this.isPropertyWarning) {
        this.isPropertyWarning = true;
        return;
      }

      this.isLoading = true;

      await axiosClient
        .put("v3/widgets/" + this.$route.params.wid, this.widget)
        .then(async () => {
          await this.loadWidget();
          this.isPropertiesUpdated = isPropertyTab;
        }).catch((error) => {
        if(error.response == undefined){
          this.errorMessage = 'Internal Server Error'
          return;
        }
        if(error.response.status === 500) {
          this.errorMessage = 'Internal Server Error'
        } else {
          this.errorMessage = 'Widget is not found'
        }
        this.isError = true;
        this.isLoading = false;
      });

      this.saveBtnVisibility = false;
      this.isSaveLabel = false;
      this.widget.edited = false;
      this.isPropertyWarning = false;
      this.isLoading = false;
    },
    async loadInfo(){
      this.isLoading = true;
      await this.loadWidget();
      this.$forceUpdate();
      this.isLoading = false;
    }
  },

  mounted() {
    this.loadInfo();
    this.loadWidgetInfoByApi();
  },

  watch: {
    isError(value: boolean) {
      if (value) {
        this.showErrorToast = true;
      }
    },
    saveBtnVisibility(value: boolean) {
      store.commit('setSaveBtnVisibility', value);
    }
  },

  data() {
    return {
      isLoading: false,
      isSaveWidget: true,
      isError: false,
      errorMessage: '',
      isUpdated: false,
      isOldWidget: false,
      isSaveLabel: false,
      widget: {} as WidgetI,
      paymentMethods: {} as PaymentMethodsI,
      widgetInfo: {},
      activeTab: TabE.GENERAL,
      saveBtnVisibility: false,
      activeIndex: 0,
      showErrorToast: false,
      showCopyToast: false,
      showUpdateWidgetModal: false,
      redirect: false,
      toPage: '',
      isPropertiesUpdated: false,
      isPropertyWarning: false,
      changeCase: changeCase,
      ElementE: ElementE,
      TabE,
      PaymentGatewayE,
    };
  },
});
</script>
